// Centralized API client with auto-refresh token handling
import { useAuthStore } from '../store/auth.store';
const API_URL = import.meta.env.VITE_API_URL || 'https://paathshaala-crm-worker.rame6.workers.dev';
export class ApiError extends Error {
    code;
    status;
    details;
    constructor(code, message, status, details) {
        super(message);
        this.code = code;
        this.status = status;
        this.details = details;
    }
}
let isRefreshing = false;
let refreshPromise = null;
async function refreshToken() {
    const { token } = useAuthStore.getState();
    if (!token)
        return null;
    try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok)
            return null;
        const data = await response.json();
        if (data.success) {
            return token;
        }
        return null;
    }
    catch {
        return null;
    }
}
async function getValidToken() {
    const { token } = useAuthStore.getState();
    if (!token)
        return null;
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return null;
        const payload = JSON.parse(atob(parts[1]));
        const exp = payload.exp * 1000;
        const now = Date.now();
        if (exp - now < 60 * 60 * 1000) {
            if (isRefreshing && refreshPromise) {
                return refreshPromise;
            }
            isRefreshing = true;
            refreshPromise = refreshToken();
            const newToken = await refreshPromise;
            isRefreshing = false;
            refreshPromise = null;
            if (newToken) {
                useAuthStore.setState({ token: newToken });
            }
            else {
                useAuthStore.getState().logout();
                return null;
            }
            return newToken;
        }
        return token;
    }
    catch {
        return token;
    }
}
async function apiRequest(url, options = {}) {
    const token = await getValidToken();
    const headers = {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_URL}${url}`, {
        ...options,
        headers,
    });
    if (response.status === 401) {
        useAuthStore.getState().logout();
        throw new ApiError('UNAUTHORIZED', 'Session expired. Please login again.', 401);
    }
    const data = await response.json();
    if (!data.success) {
        throw new ApiError(data.error?.code || 'UNKNOWN', data.error?.message || 'An error occurred', response.status, data.error?.details);
    }
    return data;
}
export const api = {
    get: (url) => apiRequest(url, { method: 'GET' }),
    post: (url, body) => apiRequest(url, { method: 'POST', body: JSON.stringify(body) }),
    put: (url, body) => apiRequest(url, { method: 'PUT', body: JSON.stringify(body) }),
    patch: (url, body) => apiRequest(url, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (url) => apiRequest(url, { method: 'DELETE' }),
    upload: async (url, formData) => {
        const token = await getValidToken();
        const headers = {};
        if (token)
            headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch(`${API_URL}${url}`, {
            method: 'POST',
            body: formData,
            headers,
        });
        if (response.status === 401) {
            useAuthStore.getState().logout();
            throw new ApiError('UNAUTHORIZED', 'Session expired. Please login again.', 401);
        }
        return response.json();
    },
};
export { API_URL };
